import React from 'react';
import { useTheme } from '@/context/theme.context';

export type GradientDividerSide = 'top' | 'bottom' | 'left' | 'right';

export interface GradientDividerProps {
  /** Extra Tailwind classes */
  className?: string;
  /** Gradient direction — inferred from `side` when omitted */
  direction?: string;
  /**
   * Explicit color stops (CSS color strings).
   * When omitted the app's brand palette from useTheme is used.
   */
  colors?: string[];
  /**
   * Pass a full CSS `linear-gradient(…)` string directly —
   * e.g. the shared `GRADIENT` / `RAINBOW` constants.
   * Overrides `colors` and `direction`.
   */
  gradient?: string;
  /** Opacity (0–1) — default: 1 */
  opacity?: number;
  /**
   * Which edge to act as an accent bar.
   * - `top` (default) → full-width 3 px horizontal stripe
   * - `bottom`        → full-width 3 px stripe at bottom
   * - `left`          → full-height 3 px vertical stripe on left
   * - `right`         → full-height 3 px vertical stripe on right
   */
  side?: GradientDividerSide;
  /** @deprecated use `side="left"` */
  vertical?: boolean;
}

const SIDE_DEFAULTS: Record<
  GradientDividerSide,
  { base: string; deg: string }
> = {
  top: { base: 'w-full h-[2px]', deg: '90deg' },
  bottom: { base: 'w-full h-[2px]', deg: '90deg' },
  left: { base: 'h-full w-[2px]', deg: '180deg' },
  right: { base: 'h-full w-[2px]', deg: '180deg' },
};

/**
 * A configurable gradient accent bar for any edge.
 *
 * ```tsx
 * <GradientDivider />                        // top stripe (default)
 * <GradientDivider side="left" />            // left vertical bar
 * <GradientDivider side="bottom" opacity={0.5} />
 * <GradientDivider gradient={GRADIENT} side="left" /> // CSS-var gradient
 * <GradientDivider colors={['red','blue']} side="right" />
 * ```
 */
export const GradientDivider = ({
  className = '',
  direction,
  colors,
  gradient,
  opacity = 1,
  side,
  vertical,
}: GradientDividerProps) => {
  const { colors: theme } = useTheme();

  // Resolve side (support legacy `vertical` prop)
  const resolvedSide: GradientDividerSide = side ?? (vertical ? 'left' : 'top');

  const { base, deg: defaultDeg } = SIDE_DEFAULTS[resolvedSide];
  const deg = direction ?? defaultDeg;

  // Resolve background
  const bg =
    gradient ??
    `linear-gradient(${deg}, ${(colors ?? [theme.primary, theme.secondary, theme.tertiary, theme.primary]).join(', ')})`;

  return (
    <div
      className={`${base} ${className}`.trim()}
      style={{ background: bg, opacity }}
    />
  );
};
